import React from 'react'
import { components } from 'react-select'
import { Box, CalendarClock, Clock, Hash, Link2, List, LucideIcon, MapPin, Paperclip, ToggleLeft, Type } from 'lucide-react'

import { IOption } from 'shared-libs/models/utils'
import 'browser/components/atomic-elements/molecules/fields/property-picker/_property-option-card.scss'

const PROPERTY_TYPE_ICONS: Record<string, LucideIcon> = {
  string: Type,
  number: Hash,
  integer: Hash,
  boolean: ToggleLeft,
  object: Box,
  edge: Link2,
  ref: Link2,
}

const DEFINITION_TYPE_ICONS: Record<string, LucideIcon> = {
  fileSet: Paperclip,
  address: MapPin,
  dateTimeWithTimezone: CalendarClock
}

function getBaseType(propertyType: string): string {
  return propertyType.replace('array-', '')
}

function getIcon(propertyType: string, definitionType?: string, isEnum?: boolean): LucideIcon | undefined {
  // Check if it's a string enum
  if (isEnum && getBaseType(propertyType) === 'string') {
    return List
  }
  const baseType = getBaseType(propertyType)
  return DEFINITION_TYPE_ICONS[definitionType || ''] || PROPERTY_TYPE_ICONS[baseType]
}

function getDisplayType(propertyType: string): string {
  if (!propertyType) return ''
  if (propertyType.startsWith('array-')) {
    return `${propertyType.replace('array-', '')}[]`
  }
  return propertyType
}

interface IPropertyOptionCardProps {
  option: IOption
}

export const PropertyOptionCard: React.FC<IPropertyOptionCardProps> = ({ option }) => {
  const { label, path, propertyType, definitionType } = option as IOption & {
    definitionType?: string
    enum?: any[]
  }

  const baseType = getBaseType(propertyType)
  const displayType = getDisplayType(propertyType)
  const isEnum = Boolean((option as any).enum)
  const Icon = getIcon(propertyType, definitionType, isEnum)

  return (
    <div className='c-propertyOptionCard'>
      <div className='c-propertyOptionCard__label'>{label}</div>

      {path && <div className='c-propertyOptionCard__path'>{path}</div>}

      <div className='c-propertyOptionCard__meta'>
        {propertyType && (
          <span className={`c-propertyOptionCard__typeLabel c-propertyOptionCard__typeLabel--${baseType}`}>
            {Icon && <Icon size={13} />}
            {displayType}
          </span>
        )}

        {definitionType && (
          <span className='c-propertyOptionCard__definitionType'>{definitionType}</span>
        )}
      </div>
    </div>
  )
}

export const PropertyOptionCardCompact: React.FC<IPropertyOptionCardProps> = ({ option }) => {
  const { label, propertyType, definitionType } = option

  const isEnum = Boolean((option as any).enum)
  const Icon = getIcon(propertyType, definitionType, isEnum)

  return (
    <div className='c-propertyOptionCard--compact'>
      <span className='c-propertyOptionCard__label'>{label}</span>
      {Icon && <Icon size={14} />}
    </div>
  )
}

export const PropertyOptionRenderer = (props: any) => {
  return (
    <components.Option {...props}>
      <PropertyOptionCard option={props.data} />
    </components.Option>
  )
}

export const PropertyValueRenderer = (props: any) => {
  return (
    <components.SingleValue {...props}>
      <PropertyOptionCardCompact option={props.data} />
    </components.SingleValue>
  )
}
